import React, {useEffect, useLayoutEffect, useState} from "react";
import {dispatch, useSelector} from "../../redux/store";
import {useSnackbar} from "notistack";
import moment from "moment/moment";
import {child, get} from "firebase/database";
import {
    getIniLoggedUser,
    getSessionAppVersion,
    getSessionSelectedLocation,
    setSessionSelectedLocation
} from "../../helper/session";

import {syncAllInvoices} from "../../redux/slices/synchronization";
import {fetchAllUserLicense, updateIsExpired} from "../../redux/slices/license";
import {fetchAllLocations} from "../../redux/slices/locations";
import {fetchAdvertisementText, fetchSettings, fetchTIN, fetchUserLogoUrl} from "../../redux/slices/settings";

import {Box, Typography} from "@mui/material";
import {Outlet} from "react-router-dom";
import Main from "./Main";
import Header from "./header";
import NavVertical from "./nav/NavVertical";
import LicenseExpiredDialog from "../../components/licenseExpiredDialog";
import CacheDialog from "../../components/cacheDialog";
import LoadingModal from "../../components/loading-modal";
import LocationDialog from "../../components/LocationDialog";

import {ALL_LICENSE, ONE_DAY_SECONDS_TIMEOUT, ONE_MINUTE_SECONDS_TIMEOUT} from "../../constants";
import {AUTH, dbRef} from "../../auth/FirebaseContext";
import {useLoading} from "../../context/Loading";
import {setDefaultUserSettings} from "../../redux/slices/userSettings";
import {USER_TYPES_GROUP} from "../../constants";
import {fetchSalesChannels} from "../../redux/slices/salesChannels";
import {fetchAllMergedItems} from "../../redux/slices/items";
import NotificationDialog from "../../components/KisokNotifications";
import {FirebaseProvider} from "../../context/FirebaseMessages";
import {fetchTaxCoreStatus} from "../../redux/slices/taxCore";
import {fetchAllMainOperators, fetchAllSubOperators} from "../../redux/slices/operatorsBe";
import {fetchStations} from "../../redux/slices/stations";
import useCameraSocket from "../../hooks/useCameraSocket";
import {fetchAllTrafficTypes} from "../../redux/slices/trafficType";
import packageJson from "../../../package.json";
import {useLocales} from "../../locales";

// ----------------------------------------------------------------------

export default function DashboardLayout() {
    const {loading} = useLoading();

    useCameraSocket();
    const {enqueueSnackbar} = useSnackbar();
    const {isWebLicenseExpired} = useSelector(((state) => state.license));
    const {settings, userImage, advertisementText} = useSelector(state => state.settings);
    const {fetched, userType} = useSelector(state => state.userSettings);
    const [open, setOpen] = useState(false);
    const [openVersionConfirm, setOpenVersionConfirm] = useState(false);
    const [openLocationDialog, setOpenLocationDialog] = useState(false);

    const primaryUser = getIniLoggedUser();
    const isPrimary = primaryUser?.uid === AUTH.currentUser.uid

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };


    const {allItemsList} = useSelector(state => state.items);

    useLayoutEffect(() => {
        dispatch(fetchStations()).unwrap().catch(reason => {
            console.error("fetchStations", reason);
            enqueueSnackbar("Greška prilikom očitavanja podataka", {variant: "error"});
        });
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        dispatch(fetchAllSubOperators())
        dispatch(fetchAllMainOperators())
        dispatch(fetchAllTrafficTypes())
    }, []);

    useEffect(() => {
        if (allItemsList.length === 0) {
            dispatch(fetchAllMergedItems()).unwrap().catch(reason => {
                console.error("fetchAllMergedItems", reason);
                enqueueSnackbar("Doslo je do greske", {variant: "error"});
            });
        }
        // eslint-disable-next-line
    }, [allItemsList.length]);


    useEffect(() => {
        dispatch(fetchTaxCoreStatus()).unwrap().catch(reason => {
            console.error("fetchTaxCoreStatus", reason);
            enqueueSnackbar("Greška prilikom očitavanja poreskih stopa!", {variant: "error"});
        });
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!settings) {
            dispatch(fetchSettings()).unwrap().catch(reason => {
                console.error("fetchSettings", reason);
                enqueueSnackbar("Greška prilikom očitavanja podešavanja!", {variant: "error"});
            });
        }
        // eslint-disable-next-line
    }, [settings]);

    useEffect(() => {
        if (!fetched && !isPrimary) {
            dispatch(setDefaultUserSettings());
        }
    }, [fetched, isPrimary])

    useEffect(() => {
        dispatch(fetchTIN()).unwrap().catch(reason => {
            console.error("fetchTIN", reason);
            enqueueSnackbar("Greška prilikom očitavanja pib-a!", {variant: "error"});
        });
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        dispatch(fetchSalesChannels()).unwrap().catch(reason => {
            console.error("fetchSalesChannels", reason);
        });
        // eslint-disable-next-line
    }, [AUTH.currentUser?.uid])

    useLayoutEffect(() => {
        if (userImage === null) {
            dispatch(fetchUserLogoUrl()).unwrap().catch((error) => {
                if (error.code !== "storage/object-not-found") {
                    console.error("fetchUserLogoUrl", error);
                    enqueueSnackbar(`Greška prilikom očitavanja loga: ${error.message}`, {
                        variant: "error",
                    });
                }
            });
        }
        // eslint-disable-next-line
    }, [userImage]);

    useEffect(() => {
        if (advertisementText === undefined) {
            dispatch(fetchAdvertisementText()).unwrap().catch(error => {
                console.error("fetchAdvertisementText", error);
                enqueueSnackbar("Greška prilikom očitavanja reklamnog teksta", {variant: "error"});
            });
        }
        // eslint-disable-next-line
    }, [advertisementText]);

    useLayoutEffect(() => {
        if (isPrimary) {
            dispatch(fetchAllLocations()).unwrap().then(response => {
                if (response.length === 1) {
                    setSessionSelectedLocation(response[0]);
                } else {
                    if (getSessionSelectedLocation() === undefined) {
                        setOpenLocationDialog(true);
                    } else {
                        let selected = response.some(location => location.PAC === getSessionSelectedLocation().PAC);
                        if (!selected) {
                            setOpenLocationDialog(true);
                        }
                    }
                }
                if (!fetched) {
                    dispatch(setDefaultUserSettings());
                }
            }).catch(reason => {
                console.error("fetchAllLocations", reason);
                enqueueSnackbar("Nije moguće očitati lokacije!!", {variant: "error"});
            });
        }
        // eslint-disable-next-line
    }, [isPrimary]);

    useEffect(() => {
        if (userType !== USER_TYPES_GROUP.CITY_TRANSPORT) {
            dispatch(syncAllInvoices()).unwrap().catch(reason => {
                console.error("syncAllInvoices", reason);
                enqueueSnackbar("Greška prilikom sinhronizacije podataka!", {variant: "error"});
            });
        }
        // eslint-disable-next-line
    }, []);

    const renderNavVertical = <NavVertical openNav={open} onCloseNav={handleClose}/>;

    const onLocationChosen = (e, location) => {
        setSessionSelectedLocation(location);
        setOpenLocationDialog(false);
        enqueueSnackbar("Uspešno dodeljena lokacija", {variant: "success"});
    };

    useEffect(() => {
        let sessionVersion = getSessionAppVersion();
        get(child(dbRef, `public/webAppData/version`)).then(value => {
            if (sessionVersion !== null || sessionVersion !== value.val()) {
                setOpenVersionConfirm(true);
            }
        });
    }, []);

    // useEffect(() => {
    //     dispatch(getIsTaxFree())
    // }, [])

    useEffect(() => {
        dispatch(fetchAllUserLicense()).unwrap().then(response => {
            if (response) {
                let webLicense = response.filter(license => license.productID === ALL_LICENSE.webESIR);
                let isWebLicenseExpired = moment(webLicense[0].expiredDate).isBefore(moment.now());
                if (!isWebLicenseExpired) {
                    let timeTrigger = dateDiffInMinutes(webLicense[0].expiredDate);
                    if (ONE_DAY_SECONDS_TIMEOUT > timeTrigger * ONE_MINUTE_SECONDS_TIMEOUT) {
                        const timer = setTimeout(() => {
                            dispatch(updateIsExpired());

                        }, timeTrigger * ONE_MINUTE_SECONDS_TIMEOUT);
                        return () => clearTimeout(timer);
                    }
                }
            }
        });
    }, [isWebLicenseExpired]);

    function dateDiffInMinutes(licenceTime) {
        let a = moment(new Date());
        let b = moment(licenceTime);
        return b.diff(a, "minutes");
    }

    return (
        <>
            {(loading || userType === undefined) && <LoadingModal/>}
            <Header onOpenNav={handleOpen}/>
            <FirebaseProvider>
                <NotificationDialog/>
                <Box
                    sx={{
                        display: {lg: "flex"},
                        minHeight: {lg: 1}
                    }}>
                    {renderNavVertical}

                    <Main>
                        <Outlet/>
                    </Main>
                </Box>

            </FirebaseProvider>
            {openLocationDialog && <LocationDialog
                open={openLocationDialog}
                handleLocationClick={onLocationChosen}/>}
            {isWebLicenseExpired && <LicenseExpiredDialog open={isWebLicenseExpired}/>}
            {openVersionConfirm && <CacheDialog open={openVersionConfirm} setOpen={setOpenVersionConfirm}/>}
            <Typography
                variant="caption"
                sx={{
                    position: "fixed",
                    bottom: 8,
                    right: 12,
                    backgroundColor: "#1976d2",
                    color: "#fff",
                    padding: "2px 8px",
                    borderRadius: "3px",
                    fontSize: "0.75rem",
                    zIndex: 2000,
                    opacity: 0.9,
                    boxShadow: "0px 2px 6px rgba(0,0,0,0.2)",
                    userSelect: "none"
                }}
            >
                verzija: {packageJson.version}
            </Typography>

        </>
    );
}
